<template>
  <div class="consulta-anual-container">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Filtros Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-center">
              <div class="col">
                <h5 class="card-title"><i class="fas fa-filter me-2"></i>Consulta por Año</h5>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <!-- Año -->
              <div class="col-md-4">
                <label class="form-label">Año</label>
                <input type="number" v-model="filtros.anio" class="form-control" min="2020" :max="currentYear">
              </div>

              <!-- Botón Buscar -->
              <div class="col-12">
                <button class="btn btn-primary me-2" @click="buscarResumen">
                  <i class="fas fa-search me-2"></i>
                  Buscar
                </button>
                <button class="btn btn-secondary" @click="limpiarFiltros">
                  <i class="fas fa-eraser me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas Generales del Año -->
    <div class="row mb-4" v-if="resumenData">
      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Total Compras</h6>
                <h3 class="mb-0">{{ formatoMoneda(totalComprasAnual) }}</h3>
              </div>
              <div class="stat-icon bg-danger">
                <i class="fas fa-shopping-cart"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Total Ventas</h6>
                <h3 class="mb-0">{{ formatoMoneda(totalVentasAnual) }}</h3>
              </div>
              <div class="stat-icon bg-success">
                <i class="fas fa-dollar-sign"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card stat-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-muted mb-2">Diferencia Total</h6>
                <h3 class="mb-0" :class="diferenciaTotalClass">{{ formatoMoneda(diferenciaTotal) }}</h3>
              </div>
              <div class="stat-icon" :class="diferenciaTotalIconClass">
                <i class="fas" :class="diferenciaTotal >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de Resumen Mensual -->
    <div class="row" v-if="resumenData">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="row align-center">
              <div class="col">
                <h5 class="card-title"><i class="fas fa-calendar-alt me-2"></i>Resumen Mensual - Año {{ resumenData.year }}</h5>
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover table-striped mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Mes</th>
                    <th class="text-end">Total Compras</th>
                    <th class="text-end">Total Ventas</th>
                    <th class="text-end">Diferencia</th>
                    <th class="text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="resumenData.resumen.length === 0">
                    <td colspan="5" class="text-center py-4">
                      <i class="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                      <p class="text-muted mb-0">No hay datos disponibles para el año seleccionado</p>
                    </td>
                  </tr>
                  <tr v-for="mes in resumenData.resumen" :key="mes.numero_mes">
                    <td class="fw-semibold">{{ mes.mes }}</td>
                    <td class="text-end text-danger fw-semibold">{{ formatoMoneda(mes.total_compras) }}</td>
                    <td class="text-end text-success fw-semibold">{{ formatoMoneda(mes.total_ventas) }}</td>
                    <td class="text-end fw-bold" :class="mes.diferencia >= 0 ? 'text-success' : 'text-danger'">
                      {{ formatoMoneda(mes.diferencia) }}
                    </td>
                    <td class="text-center">
                      <span class="badge" :class="mes.diferencia >= 0 ? 'bg-success' : 'bg-danger'">
                        <i class="fas" :class="mes.diferencia >= 0 ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
                        {{ mes.diferencia >= 0 ? 'Ganancia' : 'Pérdida' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <tfoot class="table-light">
                  <tr class="fw-bold">
                    <td>TOTAL ANUAL</td>
                    <td class="text-end text-danger">{{ formatoMoneda(totalComprasAnual) }}</td>
                    <td class="text-end text-success">{{ formatoMoneda(totalVentasAnual) }}</td>
                    <td class="text-end" :class="diferenciaTotal >= 0 ? 'text-success' : 'text-danger'">
                      {{ formatoMoneda(diferenciaTotal) }}
                    </td>
                    <td class="text-center">
                      <span class="badge" :class="diferenciaTotal >= 0 ? 'bg-success' : 'bg-danger'">
                        {{ diferenciaTotal >= 0 ? 'Ganancia Anual' : 'Pérdida Anual' }}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mensaje inicial cuando no hay datos -->
    <div class="row" v-if="!resumenData && !isLoading">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center py-5">
            <i class="fas fa-chart-bar fa-4x text-muted mb-3"></i>
            <h5 class="text-muted">Selecciona un año y haz clic en "Buscar" para ver el resumen anual</h5>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: 'ConsultaAnual',
  data() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    return {
      isLoading: false,
      currentYear,
      filtros: {
        anio: currentYear
      },
      resumenData: null
    };
  },
  computed: {
    totalComprasAnual() {
      if (!this.resumenData || !this.resumenData.resumen) return 0;
      return this.resumenData.resumen.reduce((sum, mes) => sum + (mes.total_compras || 0), 0);
    },
    totalVentasAnual() {
      if (!this.resumenData || !this.resumenData.resumen) return 0;
      return this.resumenData.resumen.reduce((sum, mes) => sum + (mes.total_ventas || 0), 0);
    },
    diferenciaTotal() {
      return this.totalVentasAnual - this.totalComprasAnual;
    },
    diferenciaTotalClass() {
      return this.diferenciaTotal >= 0 ? 'text-success' : 'text-danger';
    },
    diferenciaTotalIconClass() {
      return this.diferenciaTotal >= 0 ? 'bg-success' : 'bg-danger';
    }
  },
  mounted() {
    // Cargar datos del año actual al iniciar
    this.buscarResumen();
  },
  methods: {
    async buscarResumen() {
      if (!this.filtros.anio) {
        this.$toast.warning('Por favor ingrese un año válido');
        return;
      }

      this.isLoading = true;

      try {
        const response = await api.get('/report/resumen-anual', {
          params: {
            year: this.filtros.anio
          }
        });

        // Verificar si la respuesta fue exitosa
        if (!response.data) {
          throw new Error('Error en la respuesta del servidor');
        }

        this.resumenData = response.data;
        this.$toast.success(`Resumen del año ${this.filtros.anio} cargado exitosamente`);
      } catch (error) {
        console.error('Error al buscar resumen anual:', error);
        this.resumenData = null;

        let errorMessage = 'Error al cargar el resumen anual';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$toast.error(errorMessage);
      } finally {
        this.isLoading = false;
      }
    },

    limpiarFiltros() {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();

      this.filtros = {
        anio: currentYear
      };

      this.buscarResumen();
    },

    formatoMoneda(value) {
      if (!value && value !== 0) return '$0,00';
      const numero = Number(value).toFixed(2);
      return '$' + numero
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  }
};
</script>

<style scoped>
.consulta-anual-container {
  padding: 0;
}

.stat-card {
  border: none;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.card {
  border: none;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.table th {
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
}

.table td {
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.table tfoot tr {
  background-color: #f8f9fa;
  border-top: 3px solid #dee2e6;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.form-label {
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-select,
.form-control {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
}

.form-select:focus,
.form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>
